import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { BadgeCheck, Info } from 'lucide-react';
import BonusOverview from './BonusOverview';
import BonusCampaigns from './BonusCampaigns';

// BONUS-QUALIFIKATION-001 (Karte §11) — globale Qualifikation der Partner-Wochenboni.
// Liest/schreibt ausschließlich /admin/bonus-qualification/config (Revision + Audit serverseitig)
// und bindet die kompakte Wochenübersicht ein. Partner sehen/ändern diese Werte nie.

export const CONFLICT_TEXT = 'Inzwischen geändert – bitte neu laden';
export const HINT_TEXT = 'Angebots- und Stornofenster gelten sofort für die aktuelle Leistung; Wochenqualifikation ab der nächsten Prüfung Montag 04:00';
export const FIELD_LABELS = Object.freeze([
  ['min_completed_rides', 'Mindestens abgeschlossene Fahrten (Vorwoche)'],
  ['min_acceptance_pct', 'Annahmequote mindestens (%)'],
  ['max_cancel_pct', 'Stornoquote unter (%)'],
  ['acceptance_window', 'Annahmequote über die letzten … Angebote'],
  ['cancel_window', 'Stornoquote über die letzten … angenommenen Fahrten'],
]);

const inp = { width: '100%', padding: '7px 10px', background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 12, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const lbl = { fontSize: 12, color: '#4b5563', fontWeight: 700, display: 'block', marginBottom: 5 };
const btn = (color = '#9c27b0') => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: color, color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' });
const box = { background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' };
const boxTitle = { fontSize: 13, fontWeight: 800, marginBottom: 10, color: '#0a0a0a' };
export default function BonusQualificationPanel({ showCampaigns = true, canManage = true }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/admin/bonus-qualification/config').then((r) => {
    setData(r.data);
    setDraft(Object.fromEntries(FIELD_LABELS.map(([name]) => [name, String(r.data.config[name])])));
    setConflict(false);
    setLoadError(false);
  }).catch(() => setLoadError(true));

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!canManage) return;
    const config = {};
    for (const [name] of FIELD_LABELS) {
      const raw = String(draft[name] ?? '').trim();
      const limit = data.limits?.[name];
      const n = /^\d+$/.test(raw) ? Number(raw) : NaN;
      if (!Number.isSafeInteger(n) || (limit && (n < limit.min || n > limit.max))) {
        toast.error(limit ? `Bitte eine ganze Zahl von ${limit.min} bis ${limit.max} eingeben` : 'Bitte ganze Zahlen eingeben');
        return;
      }
      config[name] = n;
    }
    setSaving(true);
    try {
      await api.patch('/admin/bonus-qualification/config', { config, expected_revision: data.revision });
      await load();
      toast.success('Übernommen');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) { setConflict(true); toast.error(CONFLICT_TEXT); }
      else if (status === 400) toast.error('Ungültige Werte – nichts gespeichert');
      else if (status === 403) toast.error('Dafür fehlt dir die Berechtigung');
      else toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loadError && !data) {
    return (
      <div style={{ ...box, marginTop: 16 }}>
        <p style={{ fontSize: 12, color: '#b91c1c', margin: '0 0 8px 0' }}>Bonus-Qualifikation konnte nicht geladen werden.</p>
        <button type="button" onClick={load} style={btn('#374151')}>Erneut laden</button>
      </div>
    );
  }
  if (!data || !draft) return <p style={{ fontSize: 12, color: '#6b7280' }}>lädt…</p>;

  const revisionBroken = data.revision === null || data.revision === undefined;

  return (
    <div data-testid="bonus-qualification-panel" style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 'clamp(12px, 3vw, 24px)', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#9c27b022', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BadgeCheck size={20} color="#9c27b0" />
        </div>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>Bonus-Qualifikation</h2>
          <p style={{ fontSize: 12, color: '#4b5563', margin: 0 }}>Schwellen und Beobachtungsfenster für die Fahrerqualifikation · gilt für alle Partner</p>
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 16px 0', display: 'flex', gap: 6, alignItems: 'center' }}>
        <Info size={13} /> {HINT_TEXT}
      </p>

      {showCampaigns && <BonusCampaigns />}
      <BonusOverview allowReset={canManage} qualificationOnly={!showCampaigns} />
      <details style={box}>
        <summary style={boxTitle}>Qualifikationsregeln bearbeiten</summary>
        <div style={boxTitle}>Schwellen</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {FIELD_LABELS.map(([name, label]) => (
            <div key={name}>
              <label htmlFor={`bonus-qual-${name}`} style={lbl}>{label}</label>
              <input id={`bonus-qual-${name}`} type="number" inputMode="numeric" step="1"
                min={data.limits?.[name]?.min} max={data.limits?.[name]?.max}
                value={draft[name]} disabled={!canManage} onChange={(e) => setDraft((p) => ({ ...p, [name]: e.target.value }))} style={inp} />
              {data.defaulted?.includes(name) && (
                <span style={{ fontSize: 11, color: '#b45309' }}>Gespeicherter Wert ungültig – Werkswert aktiv</span>
              )}
            </div>
          ))}
        </div>
        {conflict && (
          <p role="alert" style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700, margin: '12px 0 0 0' }}>{CONFLICT_TEXT}</p>
        )}
        {revisionBroken && (
          <p role="alert" style={{ fontSize: 12, color: '#b91c1c', margin: '12px 0 0 0' }}>Revision unlesbar – Speichern gesperrt.</p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" onClick={save} disabled={!canManage || saving || revisionBroken}
            style={{ ...btn(), opacity: (!canManage || saving || revisionBroken) ? 0.6 : 1 }}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
          {conflict && <button type="button" onClick={load} style={btn('#374151')}>Neu laden</button>}
        </div>
      </details>

    </div>
  );
}
